import React from 'react';

import { useAppSelector } from '@/store';

import { BusinessValue, ThisMonth, YourAsset, VsNetwork, LookingAhead, PulseCalibrating } from './components';
import { usePulseData } from './usePulseData';
import { useShowInProgress } from '../../hooks/useShowInProgress';
import { PulsePage } from './pulse.styles';

/**
 * Pulse dashboard body (no PageShell) — the business-overview dashboard, rendered under the Pulse tab
 * of the Stats page. Wired to the Owners Hub Stats API via {@link usePulseData}; in-progress cells are
 * gated by `show_in_progress` (see `missing-data.md`). Brand switching mirrors Stats / Home.
 */
export const PulseContent = (): React.ReactElement => {
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand);
  const { data, loading, calibrating, calibrationDaysLeft } = usePulseData(displayBrand?.id);
  const showInProgress = useShowInProgress();
  // VS. Network is fully placeholder today, so the whole section is gated.
  const showNetwork = !data.network.missing || showInProgress;

  return (
    <PulsePage $calibrating={calibrating}>
      {calibrating ? (
        // Brand-new / no-revenue brands: the full-bleed "Calibrating" panel instead of empty cards.
        <PulseCalibrating daysLeft={calibrationDaysLeft} />
      ) : (
        <>
          <BusinessValue
            value={data.businessValue.value}
            delta={data.businessValue.delta}
            deltaDown={data.businessValue.deltaDown}
            sub={data.businessValue.sub}
            series={data.businessValue.series}
            months={data.businessValue.months}
            loading={loading.businessValue}
          />
          <ThisMonth metrics={data.thisMonth} showInProgress={showInProgress} loading={loading.thisMonth} />
          <YourAsset
            players={data.asset.players}
            segments={data.asset.segments}
            segmentsMissing={data.asset.segmentsMissing}
            reach={data.asset.reach}
            showInProgress={showInProgress}
            loading={loading.asset}
          />
          {showNetwork ? (
            <VsNetwork
              brands={data.network.brands}
              rank={data.network.rank}
              top={data.network.top}
              rows={data.network.rows}
              missing={data.network.missing}
              loading={loading.network}
            />
          ) : null}
          <LookingAhead
            value={data.lookingAhead.value}
            span={data.lookingAhead.span}
            sub={data.lookingAhead.sub}
            items={data.lookingAhead.items}
            itemsMissing={data.lookingAhead.itemsMissing}
            showInProgress={showInProgress}
            loading={loading.lookingAhead}
          />
        </>
      )}
    </PulsePage>
  );
};

export default PulseContent;
